import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { login } from '../../helpers/api/api';
import { AccountTypeId } from '../../model/account/accountTypeId';
import { CheckUserCredentialsResponse } from '../../model/account/checkUserCredentialsResponse';
import {
  checkCredentials,
  checkCredentialsError,
  checkCredentialsSuccess,
  clearAccountError,
  loginUser,
  loginUserError,
  loginUserSuccess,
  setRememberMe,
} from '../../redux/actions/accountActions';
import { useAppThunkDispatch } from '../../redux/configureStore';
import {
  selectAccountError,
  selectAccountIsLoading,
  selectAccountRememberMe,
} from '../../redux/selectors/accountSelectors';
import accountService from '../../services/AccountService';
import ErrorDisplay from '../ErrorHandling/ErrorDisplay';

const LoginForm: React.FC = () => {
  const { t } = useTranslation('auth');
  let navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const isLoading = useSelector(selectAccountIsLoading);
  const rememberMe = useSelector(selectAccountRememberMe);
  const error = useSelector(selectAccountError);
  const dispatch = useAppThunkDispatch();

  const onSubmit = async (e: any) => {
    e.preventDefault();
    dispatch(clearAccountError());
    dispatch(checkCredentials());
    accountService
      .checkCredentials(username, password)
      .then((response: CheckUserCredentialsResponse) => {
        //TODO: This is a bit of a hack, the password needs the be stored to use after the pin entry
        response.email = username;
        response.password = password;

        dispatch(checkCredentialsSuccess(response));

        if (!response.isTwoFactorAuthenticationEnabled) {
          dispatch(loginUser());
          login(username, password)
            .then((response: any) => {
              dispatch(loginUserSuccess(response));
              if (
                response.accountType.accountTypeId ===
                AccountTypeId.HealthcareProfessional
              ) {
                navigate('/dashboard/patients');
              } else {
                navigate(`/dashboard/patients/${response.accountGuid}`);
              }
            })
            .catch((error) => {
              dispatch(loginUserError(error.body.title));
            });
        }
      })
      .catch((error) => {
        dispatch(checkCredentialsError(error.body));
      });
  };

  const onForgotPassword = (e: any) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  return (
    <>
      <Typography variant="h4">
        <strong>{t('login.title')}</strong>
      </Typography>
      <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={onSubmit}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label={t('login.email')}
          name="username"
          autoComplete="username"
          autoFocus
          onChange={(e) => setUsername(e.target.value)}
          sx={{ '& .Mui-focused legend': { paddingRight: 20 } }}
          error={error !== null}
        />
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="password"
          label={t('login.password')}
          type="password"
          id="password"
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          sx={{ '& .Mui-focused legend': { paddingRight: 10 } }}
          error={error !== null}
        />
        {error !== null && <ErrorDisplay error={error} />}

        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="rememberMe"
                color="primary"
                checked={rememberMe}
                onChange={(e) => dispatch(setRememberMe(!rememberMe))}
              />
            }
            label={t('login.rememberMe')}
          />
        </Grid>

        <LoadingButton
          type="submit"
          loading={isLoading}
          variant="contained"
          color="primary"
          sx={{ mt: 3, mb: 2 }}
        >
          {t('login.submit')}
        </LoadingButton>

        <Grid container>
          <Grid size="grow" sx={{ mt: 4 }}>
            <Link
              sx={{ cursor: 'pointer' }}
              variant="body2"
              onClick={onForgotPassword}
            >
              {t('login.forgotPassword')}
            </Link>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default LoginForm;
